import { showTypekitOnboardDialog } from '../components/dialogs/TypekitOnboardDialog.ts';

import { handleAuthenticationWithPostLoginAction } from './authentication_util.js';
import { default as POST_LOGIN_ACTION_TYPE } from './post_login_actions/post_login_action_type.js';
import { get } from './secure_fetch.js';

/**
 * Service class for adding fonts to web projects.
 * Uses dependency injection for better testability.
 */
export class AddToWebProjectService {
  constructor() {
    this.showTypekitOnboardDialog = showTypekitOnboardDialog;
    this.handleAuthenticationWithPostLoginAction = handleAuthenticationWithPostLoginAction;
    this.get = get;
    this.POST_LOGIN_ACTION_TYPE = POST_LOGIN_ACTION_TYPE;
    this.isDialogOpen = false;
  }
  /**
   * Add family to web project with authentication
   */
  async addFamilyToWebProjectWithAuthentication(familySlug, variationSlug, editorSaveState) {
    await this.handleAuthenticationWithPostLoginAction(this.POST_LOGIN_ACTION_TYPE.ADD_TO_WEB_PROJECT, {
      familySlug: familySlug,
      variationSlug: variationSlug
    });

    return this.addFamilyToWebProject(familySlug, variationSlug, editorSaveState);
  }

  /**
   * Add the family to a web project
   */
  async addFamilyToWebProject(familySlug, variationSlug, editorSaveState) {
    const data = await this.getFamilyDataForAddToWebProjectDialog(familySlug, variationSlug);
    if (data.errors) {
      return;
    }

    await this.showAddFontsToWebProjectDialog(
      data.family.slug,
      data.family.fonts,
      editorSaveState
    );
  }

  /**
   * Display the add to fonts web project dialog
   */
  async showAddFontsToWebProjectDialog(familySlug, fonts, editorSaveState) {
    if (this.isDialogOpen) {
      return;
    }

    this.isDialogOpen = true;
    await this.showTypekitOnboardDialog();
    const { sortedFamilies, familyAndProjectsInfoForSelector } = await this.init(familySlug, fonts);

    await this.openAddToWebProjectDialog(sortedFamilies, familyAndProjectsInfoForSelector, editorSaveState);
  }

  /**
   * Open the add to web project dialog
   */
  async openAddToWebProjectDialog(families, familyAndProjectsInfoForSelector, editorSaveState) {
    const { openAddToWebProjectDialog } = await import('../controllers/add_to_web_project_dialog.js');
    const dialogPromise = openAddToWebProjectDialog({ families, familyAndProjectsInfoForSelector, editorSaveState });

    // Reset flag when dialog closes
    dialogPromise.finally(() => {
      this.isDialogOpen = false;
    });

    return dialogPromise;
  }

  /**
   * Get family data for add to web project dialog
   */
  async getFamilyDataForAddToWebProjectDialog(familySlug, variationSlug) {
    let url = '/web_project/add_to_web_project_data/' + familySlug;
    if (variationSlug) {
      url += '?variation=' + variationSlug;
    }

    const response = await this.get(url);
    return await response.json();
  }

  /**
   * Initialize data for the add to web project dialog
   */
  async init(familySlug, fonts) {
    const groupedFamiliesInfo = this.groupByTypekitFontFamilies(fonts);
    const familyAndProjectsInfo = await this.getFamilyAndProjectsInfo(familySlug);
    const familyAndProjectsInfoForSelector = {
      projects: familyAndProjectsInfo.projects,
      is_family_CJK: familyAndProjectsInfo.is_family_CJK
    };

    return {
      sortedFamilies: groupedFamiliesInfo.orderedFamilies,
      familyAndProjectsInfoForSelector
    };
  }

  /**
   * Group fonts by typekit font families
   */
  groupByTypekitFontFamilies(fonts) {
    const families = {};
    const orderedFamilyIds = [];
    fonts.forEach((font) => {
      if (!families[font.family.web_id]) {
        families[font.family.web_id] = {
          web_id: font.family.web_id,
          web_name: font.family.web_name,
          variations: []
        };
        orderedFamilyIds.push(font.family.web_id);
      }
      families[font.family.web_id].variations.push(font);
    });

    const orderedFamilies = orderedFamilyIds.map((familyId) => families[familyId]);

    return {
      familyIds: orderedFamilyIds,
      orderedFamilies: orderedFamilies
    };
  }

  /**
   * Get family and projects info
   */
  async getFamilyAndProjectsInfo(familySlug) {
    const response = await this.get('/web_project/family_and_projects_info.json', {
      params: { family_slug: familySlug }
    });
    return await response.json();
  }
}

const addToWebProjectService = new AddToWebProjectService();

// Export individual functions for backward compatibility
export const addFamilyToWebProjectWithAuthentication = (familySlug, variationSlug, editorSaveState) =>
  addToWebProjectService.addFamilyToWebProjectWithAuthentication(familySlug, variationSlug, editorSaveState);

export const addFamilyToWebProject = (familySlug, variationSlug, editorSaveState) =>
  addToWebProjectService.addFamilyToWebProject(familySlug, variationSlug, editorSaveState);
